import { useState } from 'react'
import type { ReactNode } from 'react'
import { formatDay, formatDayLong, formatTime } from '../lib/format'
import { AirlineLogo } from './AirlineLogo'
import type { Hotel, TourCrew, WithId } from '../types/models'

/**
 * Cards open before they edit.
 *
 * The native app expands a card in place to reveal its detail and only then
 * offers Edit and Delete, and these do the same. Going straight to a form on
 * one click means you can't read a record without being in a position to
 * change it — which is wrong for the viewers who make up most of a tour party.
 */

function Actions({
  canEdit,
  onEdit,
  onDelete,
  editLabel = 'Edit',
}: {
  canEdit: boolean
  onEdit: () => void
  onDelete: () => void
  editLabel?: string
}) {
  if (!canEdit) return null
  return (
    <div className="row" style={{ gap: 8 }}>
      <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onEdit}>
        {editLabel}
      </button>
      <button className="btn btn-danger" style={{ flex: 1 }} onClick={onDelete}>
        Delete
      </button>
    </div>
  )
}

function Detail({ label, value }: { label: string; value: ReactNode }) {
  if (!value) return null
  return (
    <div className="stack" style={{ gap: 1 }}>
      <span className="t-eyebrow">{label}</span>
      <span className="t-body">{value}</span>
    </div>
  )
}

/* ----------------------------------------------------------- itinerary row */


/* ------------------------------------------------------------- hotel card */

export function HotelCard({
  hotel,
  crew,
  canEdit,
  onEdit,
  onDelete,
}: {
  hotel: WithId<Hotel>
  crew: WithId<TourCrew>[]
  canEdit: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const nights = Math.max(
    1,
    Math.round(
      (hotel.checkOutDate.toMillis() - hotel.checkInDate.toMillis()) / 86_400_000,
    ),
  )

  return (
    <div className="panel" style={{ overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        className="row"
        style={{ width: '100%', padding: '14px 16px', gap: 16, textAlign: 'left' }}
      >
        <span className="grow" style={{ minWidth: 0 }}>
          <span style={{ fontWeight: 600, display: 'block' }}>{hotel.name}</span>
          <span className="t-caption truncate" style={{ display: 'block' }}>
            {[hotel.city, hotel.country].filter(Boolean).join(', ')}
          </span>
        </span>
        <span className="t-spec-caption dim">
          {formatDay(hotel.checkInDate, hotel.timezone)} –{' '}
          {formatDay(hotel.checkOutDate, hotel.timezone)}
        </span>
        <span className="chip">
          {nights} night{nights === 1 ? '' : 's'}
        </span>
      </button>

      {open && (
        <div
          className="stack"
          style={{
            padding: 16,
            gap: 12,
            background: 'var(--panel-raised)',
            borderTop: '1px solid var(--hairline)',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <Detail
              label="Check in"
              value={`${formatDayLong(hotel.checkInDate, hotel.timezone)} · ${formatTime(hotel.checkInDate, hotel.timezone)}`}
            />
            <Detail
              label="Check out"
              value={`${formatDayLong(hotel.checkOutDate, hotel.timezone)} · ${formatTime(hotel.checkOutDate, hotel.timezone)}`}
            />
            <Detail label="Address" value={hotel.address} />
            <Detail label="Booking reference" value={hotel.bookingReference} />
          </div>

          {hotel.rooms.length > 0 && (
            <div className="stack" style={{ gap: 6 }}>
              <span className="t-eyebrow">Rooms</span>
              {hotel.rooms.map((room) => (
                <div key={room.id} className="spread">
                  <span className="t-body">{room.roomNumber || 'Unassigned room'}</span>
                  <span className="t-caption">
                    {room.guests
                      .map(
                        (guest) =>
                          guest.name ||
                          crew.find((member) => member.id === guest.crewId)?.name ||
                          'Unknown',
                      )
                      .join(', ') || 'No guests'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {hotel.address && (
            <a
              className="btn btn-quiet"
              style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
              href={`https://maps.google.com/?q=${encodeURIComponent(`${hotel.name} ${hotel.address} ${hotel.city}`)}`}
              target="_blank"
              rel="noreferrer noopener"
            >
              Open in Maps ↗
            </a>
          )}

          <Actions canEdit={canEdit} onEdit={onEdit} onDelete={onDelete} editLabel="Edit hotel" />
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------- crew card */


/** Re-exported so screens import their cards from one place. */
export { AirlineLogo }
